// src/components/MyMaterialsPage.js
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import { auth } from '../firebaseConfig';
import { API_BASE_URL, fetchNewMaterialCategories } from '../services/api';
import EditCompositeModal from './EditCompositeModal';
import GuideModal from './GuideModal';
import './MyMaterialsPage.css';
import { useCompanyRole } from '../hooks/useCompanyRole';
import ContextMenu from './ContextMenu';
import { gotoMaterialUrl } from '../utils/nezoApp';
import { buildCopyRequest } from '../utils/copyRequest';


/* ────────────────────────────────────────────
   Confirm Modal
   ──────────────────────────────────────────── */

const ConfirmModal = ({ isOpen, title, message, confirmLabel = 'Confirm', confirmStyle = {}, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="mm-modal-overlay" onClick={onCancel}>
      <div className="mm-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="mm-modal__header" style={{ borderBottom: '1px solid #fee2e2' }}>
          <h2 style={{ fontSize: 18 }}>{title}</h2>
          <button className="mm-modal__close" onClick={onCancel}>×</button>
        </div>
        <div className="mm-modal__body" style={{ padding: '20px 24px' }}>
          <p style={{ color: '#374151', lineHeight: 1.6 }}>{message}</p>
        </div>
        <div className="mm-modal__footer">
          <div />
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="mm-modal__btn" onClick={onCancel}>Cancel</button>
            <button
              className="mm-modal__btn"
              style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', fontWeight: 600, ...confirmStyle }}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────── */

const timeAgo = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const diff = (new Date() - new Date(dateStr)) / 1000;
  if (diff < 60)     return 'Just now';
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const statusConfig = {
  Pending:   { color: '#f59e0b', bg: '#fef3c7', icon: '⏳', label: 'Pending Review' },
  Draft:     { color: '#8b5cf6', bg: '#f5f3ff', icon: '📝', label: 'Draft' },
  Approved:  { color: '#10b981', bg: '#ecfdf5', icon: '✅', label: 'Approved' },
  Rejected:  { color: '#ef4444', bg: '#fef2f2', icon: '❌', label: 'Rejected' },
  Dismissed: { color: '#6b7280', bg: '#f3f4f6', icon: '🗑️', label: 'Dismissed' },
  Completed: { color: '#6366f1', bg: '#eef2ff', icon: '🎉', label: 'Completed' },
  ForReview: { color: '#92400e', bg: '#fffbeb', icon: '🔍', label: 'For Review' },
  DeletionRequested: { color: '#b91c1c', bg: '#fef2f2', icon: '🗑️', label: 'Deletion Requested' },
  Deleted: { color: '#6b7280', bg: '#f3f4f6', icon: '🗑️', label: 'Deleted' },
};

const getStatus = (s) =>
  statusConfig[s] || { color: '#6b7280', bg: '#f3f4f6', icon: '📋', label: s || 'Unknown' };

const regionNameMap = {
  'vquest':        'New Zealand',
  'vquest-au-1':   'Australia',
  'vquest-uk-1':   'United Kingdom',
  'vquest-uk-ICE': 'UK - ICE',
  'vquest-us-1':   'USA/Canada',
  'vquest-irl-1':  'Ireland',
  'vquest-midrise':'Mid Rise',
};

// ── Status tabs config ────────────────────────────────────────────────────
const STATUS_TABS = [
  { key: 'Pending',   label: 'Pending',   icon: '⏳' },
  { key: 'Draft',     label: 'Draft',     icon: '📝' },
  { key: 'ForReview', label: 'For Review', icon: '🔍' },
  { key: 'Approved',  label: 'Approved',  icon: '✅' },
  { key: 'Rejected',  label: 'Rejected',  icon: '❌' },
  { key: 'Dismissed', label: 'Dismissed', icon: '🗑️' },
   { key: 'DeletionRequested', label: 'Deletion Requested', icon: '🚨' },
  { key: 'Deleted',   label: 'Deleted',   icon: '🗑️' },
];

// ── Visibility badge ──────────────────────────────────────────────────────
const VisibilityBadge = ({ visibility }) => {
  const isPrivate = visibility === 'private';
  return (
    <span className={`mm-visibility-badge ${isPrivate ? 'mm-visibility-badge--private' : 'mm-visibility-badge--public'}`}>
      {isPrivate ? '🔒 Private' : '🌐 Public'}
    </span>
  );
};

// ── People pills: who created it, who sent it for approval ──
const PeoplePills = ({ item }) => {
  const creator = item.SubmittedByEmail;
  // ReviewedBy is "last actioned by". Only treat it as the sender while Pending,
  // because once Approved/Rejected it becomes the Nezo admin who actioned it.
  const sentBy = item.Status === 'Pending' ? item.ReviewedBy : null;
  const shortName = (e) => (e && e.includes('@') ? e.split('@')[0] : e || '—');

  return (
    <div className="mm-people-pills">
      {creator && (
        <span className="mm-people-pill mm-people-pill--creator" title={`Created by ${creator}`}>
          👤 {shortName(creator)}
        </span>
      )}
      {sentBy && sentBy !== creator && (
        <span className="mm-people-pill mm-people-pill--sender" title={`Sent for approval by ${sentBy}`}>
          🚀 {shortName(sentBy)}
        </span>
      )}
    </div>
  );
};

// ── Inner status tabs bar ─────────────────────────────────────────────────
const StatusTabs = ({ items, activeStatus, onChange }) => {
  const counts = STATUS_TABS.reduce((acc, { key }) => {
    acc[key] = items.filter(r => r.Status === key || (key === 'Approved' && r.Status === 'Completed')).length;
    return acc;
  }, {});

  return (
    <div className="mm-status-tabs">
      {STATUS_TABS.map(({ key, label, icon }) => {
        const count    = counts[key] ?? 0;
        const isActive = activeStatus === key;
        return (
          <button
            key={key}
            className={`mm-status-tab${isActive ? ' mm-status-tab--active' : ''}`}
            onClick={() => onChange(key)}
          >
            <span className="mm-status-tab__icon">{icon}</span>
            <span className="mm-status-tab__label">{label}</span>
            {count > 0 && (
              <span className={`mm-status-tab__badge${isActive ? ' mm-status-tab__badge--active' : ''}`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/* ────────────────────────────────────────────
   Detail Modal
   ──────────────────────────────────────────── */

const COPYABLE_STATUSES = ['Approved', 'Completed', 'Draft', 'ForReview'];

const DetailModal = ({ item, type, onClose, onEditRequest, onDeleteDraft, onCancel, onRequestDeletion, onUpdateRequest, onCopy, canReview, onLoadToNezo, onCompanyDismiss, resolveCategory = (c) => c || '' }) => {
  if (!item) return null;

  const isProduct  = type === 'product';
  const materialData = item.MaterialData      || {};
  const envData      = item.EnvironmentalData || {};
  const visibility   = item.Visibility        || 'public';
  const canEdit = !isProduct && (
    item.Status === 'Draft' ||
    item.Status === 'ForReview' ||
    (item.Status === 'Pending' && canReview)
  );
  const isDraft      = item.Status === 'Draft';
  

  return (
    <div className="mm-modal-overlay" onClick={onClose}>
      <div className="mm-modal" onClick={(e) => e.stopPropagation()}>

        <div className="mm-modal__header">
          <h2>{isProduct ? '📦 Product Submission' : '🏗️ Composite Request'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <PeoplePills item={item} />
            <VisibilityBadge visibility={visibility} />
            {canEdit && (
              <button
                className="mm-edit-btn"
                onClick={(e) => { e.stopPropagation(); onEditRequest(item); }}
                title="Edit line items and resubmit for review"
              >
                ✏️ Edit &amp; Resubmit
              </button>
            )}
            <button className="mm-modal__close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="mm-modal__body">

          {/* Status banner */}
          <div className="mm-status-banner" style={{ background: getStatus(item.Status).bg }}>
            <span className="mm-status-banner__icon">{getStatus(item.Status).icon}</span>
            <div>
              <div className="mm-status-banner__label">Status</div>
              <div className="mm-status-banner__value" style={{ color: getStatus(item.Status).color }}>
                {getStatus(item.Status).label}
              </div>
            </div>
            <div className="mm-status-banner__visibility">
              <div className="mm-status-banner__label">Visibility</div>
              <div
                className="mm-status-banner__value"
                style={{ color: visibility === 'private' ? '#92400E' : '#065F46' }}
              >
                {visibility === 'private' ? 'Your account only' : 'All users in region'}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div className="mm-status-banner__label">Submitted</div>
              <div className="mm-status-banner__value">
                {item.SubmittedAt ? new Date(item.SubmittedAt).toLocaleDateString() : 'N/A'}
              </div>
            </div>
          </div>

          {canEdit && (
            <div className="mm-edit-nudge">
              <span className="mm-edit-nudge__icon">💡</span>
              <span>
                {isDraft
                  ? 'This is a draft — finish adding line items and submit when ready.'
                  : 'This is a private material — you can edit its build-up and resubmit at any time.'}
              </span>
              <button
                className="mm-edit-nudge__btn"
                onClick={(e) => { e.stopPropagation(); onEditRequest(item); }}
              >
                Edit &amp; Resubmit →
              </button>
            </div>
          )}

          {item.ReviewNotes && (
            <div className={`mm-review-notes mm-review-notes--${item.Status?.toLowerCase()}`}>
              <span className="mm-review-notes__label">
                {item.Status === 'Rejected' ? '⚠️ Rejection reason' : '💬 Admin notes'}
              </span>
              <p className="mm-review-notes__text">{item.ReviewNotes}</p>
            </div>
          )}

          {isProduct ? (
            <>
              <div className="mm-detail-section">
                <h3>Product Information</h3>
                <div className="mm-detail-grid">
                  <div className="mm-detail-item"><span className="mm-detail-label">Name</span><span className="mm-detail-value">{materialData.Name || 'N/A'}</span></div>
                  <div className="mm-detail-item"><span className="mm-detail-label">Description</span><span className="mm-detail-value">{materialData.Description || 'N/A'}</span></div>
                  <div className="mm-detail-item"><span className="mm-detail-label">Category</span><span className="mm-detail-value">{resolveCategory(materialData.CategoryId || materialData.Category) || 'N/A'}</span></div>
                  <div className="mm-detail-item"><span className="mm-detail-label">Quantity Type</span><span className="mm-detail-value">{materialData.QuantityType || materialData.QuantityTypeName || 'N/A'}</span></div>
                  <div className="mm-detail-item"><span className="mm-detail-label">Region</span><span className="mm-detail-value">{item.RegionName || 'N/A'}</span></div>
                  <div className="mm-detail-item"><span className="mm-detail-label">Visibility</span><span className="mm-detail-value">{visibility === 'private' ? '🔒 Private to your account' : '🌐 Public to all users'}</span></div>
                </div>
              </div>
              {envData && Object.keys(envData).length > 0 && (
                <div className="mm-detail-section">
                  <h3>Environmental Data</h3>
                  <div className="mm-env-grid">
                    {Object.entries(envData).map(([key, data]) => (
                      <div key={key} className="mm-env-card">
                        <div className="mm-env-card__icon">
                          {key === 'carbon' ? '🌡️' : key === 'biogenic' ? '🌱' : key === 'weight' ? '⚖️' : key === 'cost' ? '💰' : '📊'}
                        </div>
                        <div className="mm-env-card__label">{key}</div>
                        <div className="mm-env-card__value">{data?.value || 'N/A'} {data?.unit || ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(item.UploadedFiles) && item.UploadedFiles.length > 0 && (
                <div className="mm-detail-section">
                  <h3>Uploaded Files ({item.UploadedFiles.length})</h3>
                  <div className="mm-files-list">
                    {item.UploadedFiles.map((file, i) => (
                      <div key={i} className="mm-file-item">
                        <span className="mm-file-icon">📄</span>
                        <span className="mm-file-name">{file.name || `File ${i + 1}`}</span>
                        {file.size && <span className="mm-file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mm-detail-section">
                <h3>Material Information</h3>
                <div className="mm-detail-grid">
                  <div className="mm-detail-item">
                    <span className="mm-detail-label">Material Name</span>
                    <span className="mm-detail-value">
                      {materialData.BaseMaterialName || materialData.OriginalMaterialName || materialData.SearchedMaterialName || materialData.Name || 'N/A'}
                    </span>
                  </div>
                  <div className="mm-detail-item">
                    <span className="mm-detail-label">Unit</span>
                    <span className="mm-detail-value">
                      {materialData.QuantityTypeName || materialData.QuantityType || 'N/A'}
                    </span>
                  </div>
                  <div className="mm-detail-item">
                    <span className="mm-detail-label">Category</span>
                    <span className="mm-detail-value">
                      {resolveCategory(materialData.CategoryId || materialData.Category) || 'N/A'}
                    </span>
                  </div>
                  <div className="mm-detail-item"><span className="mm-detail-label">Region</span><span className="mm-detail-value">{item.RegionName || 'N/A'}</span></div>
                  <div className="mm-detail-item">
                    <span className="mm-detail-label">Line Items</span>
                    <span className="mm-detail-value">{Array.isArray(item.LineItemsData) ? item.LineItemsData.length : 0}</span>
                  </div>
                  <div className="mm-detail-item">
                    <span className="mm-detail-label">Visibility</span>
                    <span className="mm-detail-value">{visibility === 'private' ? '🔒 Private to your account' : '🌐 Public to all users'}</span>
                  </div>
                </div>
              </div>
              {Array.isArray(item.LineItemsData) && item.LineItemsData.filter(li => !String(li.measurementId || li.code || '').toLowerCase().includes('rate')).length > 0 && (() => {
                const grouped = [];
                let currentHeading    = null;
                let currentSubHeading = null;   // 🆕
                const materialMap     = new Map();
 
                const flushMap = () => {
                  materialMap.forEach((m) => grouped.push(m));
                  materialMap.clear();
                };
 
                const envLineItems = item.LineItemsData.filter(li => {
                  const code = String(li.measurementId || li.code || '').toLowerCase();
                  return !code.includes('rate');
                });
 
                envLineItems.forEach((li) => {
                  const itemHeading    = li.heading    || null;
                  const itemSubHeading = li.sub_heading || li.subHeading || null;  // 🆕
 
                  // ── Heading change ──────────────────────────────────────────
                  if (itemHeading !== currentHeading) {
                    flushMap();
                    if (itemHeading) grouped.push({ _type: 'heading', label: itemHeading });
                    currentHeading    = itemHeading;
                    currentSubHeading = null;           // 🆕 reset sub-heading
                  }
 
                  // ── Sub-heading change 🆕 ───────────────────────────────────
                  if (itemSubHeading && itemSubHeading !== currentSubHeading) {
                    flushMap();                         // flush pending material rows first
                    grouped.push({ _type: 'subheading', label: itemSubHeading });
                    currentSubHeading = itemSubHeading;
                  }
 
                  const key = li.description || li.id;
                  if (!materialMap.has(key)) {
                    materialMap.set(key, {
                      _type:       'material',
                      description: li.description || '—',
                      unit:        li.unit        || '',
                      quantity:    li.quantity     ?? '—',
                      user_added:  li.user_added || li.isUserAdded || li.isUserAdjusted,
                      carbon: null, biogenic: null, weight: null, cost: null,
                    });
                  }
                  const entry = materialMap.get(key);
                  const code  = String(li.measurementId || li.code || '').toLowerCase();
                  if      (code.includes('biogenic')) entry.biogenic = li.total ?? li.value;
                  else if (code.includes('co2'))      entry.carbon   = li.total ?? li.value;
                  else if (code.includes('weight'))   entry.weight   = li.total ?? li.value;
                  else if (code.includes('rate'))     entry.cost     = li.total ?? li.value;
                });
                flushMap();
 
                const materialRows = grouped.filter(g => g._type === 'material');
                return (
                  <div className="mm-detail-section">
                    <h3>Line Items ({materialRows.length})</h3>
                    <div className="mm-line-items-view">
                      <div className="mm-li-header">
                        <span className="mm-li-header__material">Material</span>
                        <span className="mm-li-header__qty">Quantity</span>
                        <span className="mm-li-header__unit">Unit</span>
                        <span className="mm-li-header__metric">🌡️ Carbon</span>
                        <span className="mm-li-header__metric">🌱 Biogenic</span>
                        <span className="mm-li-header__metric">⚖️ Weight</span>
                      </div>
                      {grouped.map((row, i) => {
 
                        // ── Heading row ────────────────────────────────────────
                        if (row._type === 'heading') {
                          return (
                            <div key={`h-${i}`} className="mm-li-heading">
                              <span className="mm-li-heading__text">📋 {row.label}</span>
                            </div>
                          );
                        }
 
                        // ── Sub-heading row 🆕 ─────────────────────────────────
                        if (row._type === 'subheading') {
                          return (
                            <div
                              key={`sh-${i}`}
                              style={{
                                padding:    '5px 12px 5px 28px',
                                background: '#f7f9fc',
                                borderLeft: '3px solid #a0bfdf',
                                fontWeight: 600,
                                fontSize:   13,
                                color:      '#2c5282',
                                margin:     '2px 0',
                              }}
                            >
                              └ {row.label}
                            </div>
                          );
                        }
 
                        // ── Material row ───────────────────────────────────────
                        const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(4));
                        return (
                          <div key={i} className={`mm-li-row${row.user_added ? ' mm-li-row--adjusted' : ''}`}>
                            <span className="mm-li-row__material">
                              {row.user_added && <span className="mm-li-row__star">⭐</span>}
                              {row.description}
                            </span>
                            <span className="mm-li-row__qty">{row.quantity}</span>
                            <span className="mm-li-row__unit-badge">{row.unit}</span>
                            <span className="mm-li-row__metric-val">{fmt(row.carbon)}</span>
                            <span className="mm-li-row__metric-val">{fmt(row.biogenic)}</span>
                            <span className="mm-li-row__metric-val">{fmt(row.weight)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>

        <div className="mm-modal__footer">
          <div className="mm-modal__id">ID: {item.RequestId}</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {canEdit && (
              <button
                className="mm-edit-btn mm-edit-btn--footer"
                onClick={(e) => { e.stopPropagation(); onEditRequest(item); }}
              >
                ✏️ Edit &amp; Resubmit
              </button>
            )}
            {isDraft && (
              <button
                className="mm-modal__btn"
                style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca' }}
                onClick={(e) => { e.stopPropagation(); onDeleteDraft(item, e); }}
              >
                🗑️ Delete Draft
              </button>
            )}
            {canReview && ['Draft', 'ForReview'].includes(item.Status) && (
              <>
                <button
                  className="mm-edit-btn mm-edit-btn--footer"
                  style={{ background: '#ecfdf5', color: '#047857', border: '1px solid #6ee7b7' }}
                  onClick={(e) => { e.stopPropagation(); onLoadToNezo(item); }}
                >
                  🚀 Load to Nezo
                </button>
                <button
                  className="mm-modal__btn"
                  style={{ background: '#f3f4f6', color: '#6b7280' }}
                  onClick={(e) => { e.stopPropagation(); onCompanyDismiss(item); }}
                >
                  🗑️ Dismiss
                </button>
              </>
            )}
            {item.Status === 'Pending' && (isProduct || canReview) && (
              <button
                className="mm-modal__btn"
                style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca' }}
                onClick={(e) => { e.stopPropagation(); onCancel(item); }}
              >
                ✖ Cancel Request
              </button>
            )}
            {item.Status === 'Approved' && item.Visibility === 'private' && (
              <button
                className="mm-modal__btn"
                style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
                onClick={(e) => { e.stopPropagation(); onRequestDeletion(item); }}
              >
                🗑️ Request Deletion
              </button>
            )}
            {item.Status === 'Approved' && (
              <button
                className="mm-edit-btn mm-edit-btn--footer"
                style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd' }}
                onClick={(e) => { e.stopPropagation(); onUpdateRequest(item); }}
              >
                ✏️ Update Material
              </button>
            )}
            {!isProduct && (item.Status === 'Approved' || item.Status === 'Completed') && item.QuantityUnitId && (
              <button
                className="mm-edit-btn mm-edit-btn--footer"
                style={{ background: '#edf8f6', color: '#2a7f73', border: '1px solid #9fd3c9' }}
                title={item.Environment === 'staging' ? 'Opens staging Nezo — this material only exists there' : 'Open this material in Nezo'}
                onClick={(e) => {
                  e.stopPropagation();
                  const name = item.MaterialData?.Name || item.MaterialData?.name || item.MaterialName || '';
                  window.open(gotoMaterialUrl(item.QuantityUnitId, item.RegionId, name, item.Environment), '_blank', 'noopener');
                }}
              >
                ↗ Go to Nezo
              </button>
            )}
            {!isProduct && onCopy && COPYABLE_STATUSES.includes(item.Status) && (
              <button
                className="mm-edit-btn mm-edit-btn--footer"
                style={{ background: '#f5f3ff', color: '#6d28d9', border: '1px solid #c4b5fd' }}
                title="Start a new material from a complete copy of this one — rename it, then submit"
                onClick={(e) => { e.stopPropagation(); onCopy(item); }}
              >
                ⧉ Copy material
              </button>
            )}
            <button className="mm-modal__btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════ */

function MyMaterialsPage() {
  const navigate           = useNavigate();
  const { user }           = useContext(AuthContext);
  const { selectedRegion, allowedRegions, updateRegion } = useRegion();

  const [loading, setLoading]                       = useState(true);
  const [error, setError]                           = useState(null);
  const [productSubmissions, setProductSubmissions] = useState([]);
  const [compositeRequests, setCompositeRequests]   = useState([]);

  const [activeTab, setActiveTab]                   = useState('composites');
  const [productStatusTab, setProductStatusTab]     = useState('Pending');
  const [compositeStatusTab, setCompositeStatusTab] = useState('Pending');

  const [selectedItem, setSelectedItem]             = useState(null);
  const [selectedType, setSelectedType]             = useState(null);
  const [visibilityFilter, setVisibilityFilter]     = useState('all');
  const [searchQuery, setSearchQuery]               = useState('');

  // View mode (grid|list) + new filters (category / unit / creator / date)
  const [viewMode, setViewMode]                     = useState('grid');
  const [categoryFilter, setCategoryFilter]         = useState('all');
  const [unitFilter, setUnitFilter]                 = useState('all');
  const [createdByFilter, setCreatedByFilter]       = useState('all');
  const [dateFilter, setDateFilter]                 = useState('all');   // preset range
  const [dateExact, setDateExact]                   = useState('');      // specific day (YYYY-MM-DD)
  const [sortBy, setSortBy]                         = useState('date');  // 'date' | 'name-asc' | 'name-desc'

  const [editItem, setEditItem]                     = useState(null);
  const [showEditModal, setShowEditModal]           = useState(false);

  const [confirmModal, setConfirmModal]             = useState({ open: false, item: null, action: null });
  const [successModal, setSuccessModal]             = useState({ open: false, title: '', message: '' });

  const [updateItem,      setUpdateItem]      = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  // "Add build-up" from the Material Library (a cost-only material): open Update Material for the
  // approved request behind that material id — same id, so Nezo mappings and the cost link stay put.
  const location = useLocation();
  const updateQuantityUnitId = location.state?.updateQuantityUnitId;
  const [autoOpenedUpdate, setAutoOpenedUpdate] = useState(false);
  useEffect(() => {
    if (!updateQuantityUnitId || autoOpenedUpdate || compositeRequests.length === 0) return;
    const target = compositeRequests.find(r =>
      r.QuantityUnitId === updateQuantityUnitId && (r.Status === 'Approved' || r.Status === 'Completed'));
    if (target) {
      setUpdateItem(target);
      setShowUpdateModal(true);
    }
    setAutoOpenedUpdate(true);
  }, [updateQuantityUnitId, autoOpenedUpdate, compositeRequests]);

  const [showTutorial,    setShowTutorial]    = useState(false);

  // id → name map so we display the readable category name instead of the UUID.
  const [categoryMap, setCategoryMap] = useState({});

  const { canReview } = useCompanyRole();

  // Resolve a stored category value (UUID or legacy name) to its display name.
  const resolveCategory = useCallback(
    (cat) => (cat ? (categoryMap[cat] || cat) : ''),
    [categoryMap],
  );

  // The display unit for an item — same fallback chain the card uses.
  const getItemUnit = useCallback(
    (item) =>
      item.MaterialData?.QuantityUnit ||
      item.MaterialData?.QuantityTypeName ||
      item.MaterialData?.QuantityType ||
      item.QuantityUnit ||
      item.QuantityTypeName ||
      '',
    [],
  );

  // The display name for an item — matches how the card resolves it.
  const getItemName = useCallback(
    (item) =>
      item.MaterialData?.Name ||
      item.MaterialData?.BaseMaterialName ||
      item.MaterialData?.OriginalMaterialName ||
      item.MaterialData?.SearchedMaterialName ||
      '',
    [],
  );

  const handleUpdateRequest = (item) => {
    setUpdateItem(item);
    setShowUpdateModal(true);
    setSelectedItem(null);
  };

  /* ── Fetch ── */
  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const headers = {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        'x-region':      selectedRegion || 'vquest',
        'x-user-role':   user.role  || 'user',
        'x-user-id':     user.uid,
        'x-user-email':  user.email,
      };

      const [matRes, compRes] = await Promise.all([
        fetch(`${API_BASE_URL}/material-requests/all`, { headers }).then((r) => (r.ok ? r.json() : { requests: [] })),
        fetch(`${API_BASE_URL}/composite-requests/all`, { headers }).then((r) => (r.ok ? r.json() : { requests: [] })),
      ]);

      setProductSubmissions(matRes.requests  || []);
      setCompositeRequests(compRes.requests  || []);
    } catch (err) {
      console.error('❌ Error loading user materials:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, selectedRegion]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Category id → name map (for display) ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await fetchNewMaterialCategories();
        if (cancelled) return;
        const map = {};
        (Array.isArray(cats) ? cats : []).forEach((c) => {
          if (c?.id) map[c.id] = c.name || c.id;
        });
        setCategoryMap(map);
      } catch (e) {
        console.error('❌ Error loading category names:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRegion]);

  /* ── Stats ── */
  const stats = useMemo(() => ({
    totalProducts:      productSubmissions.length,
    totalComposites:    compositeRequests.length,
    pendingProducts:    productSubmissions.filter((r) => r.Status === 'Pending').length,
    approvedProducts:   productSubmissions.filter((r) => r.Status === 'Approved').length,
    pendingComposites:  compositeRequests.filter((r) => r.Status === 'Pending').length,
    approvedComposites: compositeRequests.filter((r) => r.Status === 'Approved' || r.Status === 'Completed').length,
    privateComposites:  compositeRequests.filter((r) => r.Visibility === 'private').length,
    publicComposites:   compositeRequests.filter((r) => r.Visibility !== 'private').length,
  }), [productSubmissions, compositeRequests]);

  /* ── Filtered items ── */
  const currentStatusTab    = activeTab === 'products' ? productStatusTab : compositeStatusTab;
  const setCurrentStatusTab = activeTab === 'products' ? setProductStatusTab : setCompositeStatusTab;
  const allItemsForTab      = activeTab === 'products' ? productSubmissions : compositeRequests;

  // Items in the active tab + status (before the extra filters) — drives both the
  // filter option lists and the final filtered set below.
  const statusItems = useMemo(
    () =>
      allItemsForTab.filter(
        (r) => r.Status === currentStatusTab || (currentStatusTab === 'Approved' && r.Status === 'Completed')
      ),
    [allItemsForTab, currentStatusTab],
  );

  // Distinct category + unit options actually present in the current status set,
  // so the dropdowns stay short and relevant.
  const categoryOptions = useMemo(() => {
    const map = new Map();
    statusItems.forEach((r) => {
      const id = r.MaterialData?.CategoryId;
      if (id && !map.has(id)) map.set(id, resolveCategory(id));
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      String(a.name).localeCompare(String(b.name)),
    );
  }, [statusItems, resolveCategory]);

  const unitOptions = useMemo(() => {
    const set = new Set();
    statusItems.forEach((r) => {
      const u = getItemUnit(r);
      if (u) set.add(u);
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [statusItems, getItemUnit]);

  // Distinct creators (submitter email) present in the current status set.
  const createdByOptions = useMemo(() => {
    const set = new Set();
    statusItems.forEach((r) => {
      const who = r.SubmittedByEmail;
      if (who) set.add(who);
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [statusItems]);

  const currentItems = useMemo(() => {
    let filtered = statusItems;
    if (activeTab === 'composites' && visibilityFilter !== 'all') {
      filtered = filtered.filter((r) => (r.Visibility || 'public') === visibilityFilter);
    }
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((r) => (r.MaterialData?.CategoryId || '') === categoryFilter);
    }
    if (unitFilter !== 'all') {
      filtered = filtered.filter((r) => getItemUnit(r) === unitFilter);
    }
    if (createdByFilter !== 'all') {
      filtered = filtered.filter((r) => (r.SubmittedByEmail || '') === createdByFilter);
    }
    if (dateExact) {
      // Filter to a single calendar day (local time), regardless of preset.
      filtered = filtered.filter((r) => {
        const d = new Date(r.SubmittedAt);
        if (Number.isNaN(d.getTime())) return false;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}` === dateExact;
      });
    } else if (dateFilter !== 'all') {
      const days = dateFilter === '7d' ? 7 : dateFilter === '30d' ? 30 : dateFilter === '90d' ? 90 : 0;
      if (days) {
        const cutoff = Date.now() - days * 86400000;
        filtered = filtered.filter((r) => {
          const t = new Date(r.SubmittedAt).getTime();
          return !Number.isNaN(t) && t >= cutoff;
        });
      }
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((r) => {
        const name = r.MaterialData?.Name || r.MaterialData?.BaseMaterialName || '';
        return (
          name.toLowerCase().includes(q) ||
          (r.RequestId  || '').toLowerCase().includes(q) ||
          (r.RegionName || '').toLowerCase().includes(q)
        );
      });
    }
    const sorted = [...filtered];
    if (sortBy === 'name-asc' || sortBy === 'name-desc') {
      sorted.sort((a, b) =>
        getItemName(a).localeCompare(getItemName(b), undefined, { sensitivity: 'base' }),
      );
      if (sortBy === 'name-desc') sorted.reverse();
    } else {
      sorted.sort((a, b) => new Date(b.SubmittedAt) - new Date(a.SubmittedAt));
    }
    return sorted;
  }, [statusItems, activeTab, visibilityFilter, categoryFilter, unitFilter, createdByFilter, dateFilter, dateExact, searchQuery, sortBy, getItemUnit, getItemName]);

  const openDetail = (item, type) => { setSelectedItem(item); setSelectedType(type); };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setVisibilityFilter('all');
    setSearchQuery('');
    setCategoryFilter('all');
    setUnitFilter('all');
    setCreatedByFilter('all');
    setDateFilter('all');
    setDateExact('');
  };

  const handleEditRequest = (item) => {
    setEditItem(item);
    setShowEditModal(true);
    setSelectedItem(null);
    setSelectedType(null);
  };

  // ── right-click menu: the same actions as the tile + detail modal, plus Go to Nezo ──
  const [contextMenu, setContextMenu] = useState(null);
  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const menuItemsFor = (item, isProduct) => {
    const canEditItem = !isProduct && (
      item.Status === 'Draft' || item.Status === 'ForReview' || (item.Status === 'Pending' && canReview));
    const approved = item.Status === 'Approved' || item.Status === 'Completed';
    const name = item.MaterialData?.Name || item.MaterialData?.name || item.MaterialName || 'material';
    const items = [
      { icon: '🔍', label: 'View details', onClick: () => openDetail(item, isProduct ? 'product' : 'composite') },
    ];
    if (canEditItem) items.push({ icon: '✏️', label: 'Edit & Resubmit', onClick: () => handleEditRequest(item) });
    if (!isProduct && approved) items.push({ icon: '🧱', label: 'Update Material (add or change the build-up)', onClick: () => handleUpdateRequest(item) });
    if (!isProduct && canReview && ['Draft', 'ForReview'].includes(item.Status)) {
      items.push({ icon: '🚀', label: 'Load to Nezo', onClick: () => setConfirmModal({ open: true, item, action: 'load-to-nezo' }) });
      items.push({ icon: '🗑️', label: 'Dismiss', onClick: () => setConfirmModal({ open: true, item, action: 'company-dismiss' }) });
    }
    if (item.Status === 'Draft') items.push({ icon: '🗑️', label: 'Delete Draft', danger: true, onClick: () => handleDeleteDraft(item) });
    if (item.Status === 'Pending' && (isProduct || canReview)) {
      items.push({ icon: '✖', label: 'Cancel Request', danger: true, onClick: () => setConfirmModal({ open: true, item, action: 'cancel' }) });
    }
    if (approved && item.Visibility === 'private') {
      items.push({ icon: '🗑️', label: 'Request Deletion', danger: true, onClick: () => setConfirmModal({ open: true, item, action: 'request-deletion' }) });
    }
    if (!isProduct && COPYABLE_STATUSES.includes(item.Status)) {
      items.push({ icon: '⧉', label: 'Copy material (start a new one from it)', onClick: () => handleCopyRequest(item) });
    }
    items[items.length - 1].dividerAfter = true;
    if (!isProduct && approved && item.QuantityUnitId) {
      items.push({
        icon: '↗',
        label: 'Go to Nezo',
        onClick: () => window.open(gotoMaterialUrl(item.QuantityUnitId, item.RegionId, name, item.Environment), '_blank', 'noopener'),
      });
    }
    if (item.QuantityUnitId) {
      items.push({ icon: '⧉', label: 'Copy material ID', onClick: () => navigator.clipboard?.writeText(item.QuantityUnitId) });
    }
    return items;
  };

  // "Copy material": the whole request — name, unit, category, line items… — onto Create New Material.
  // Line-item codes and categories are per region, so switch to the material's region first.
  function handleCopyRequest(item) {
    const copy = buildCopyRequest(item);
    if (copy.regionId && copy.regionId !== selectedRegion) {
      if (allowedRegions?.length && !allowedRegions.includes(copy.regionId)) {
        alert(`This material is in a region you don't have access to (${item.RegionName || copy.regionId}), so it can't be copied.`);
        return;
      }
      updateRegion(copy.regionId);
    }
    setSelectedItem(null);
    setSelectedType(null);
    navigate('/composite-requests', { state: { copyRequest: copy } });
  }

  const openContextMenu = (e, item, isProduct) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, items: menuItemsFor(item, isProduct) });
  };

  const handleEditClose   = () => { setShowEditModal(false); setEditItem(null); };
  const handleResubmitted = () => { loadData(); };

  const handleDeleteDraft = (item, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({ open: true, item });
  };

  const handleConfirmAction = async () => {
  const { item, action } = confirmModal;
  setConfirmModal({ open: false, item: null, action: null });
  if (!item) return;

  try {
    const token = await auth.currentUser?.getIdToken();
    const headers = {
      'Authorization': `Bearer ${token}`,
      'x-user-id':    user.uid,
      'x-user-role':  user.role || 'user',
      'x-user-email': user.email,
      'x-region':     selectedRegion || 'vquest',
    };

    if (action === 'cancel') {
      // ✅ Route to correct endpoint based on active tab
      const endpoint = activeTab === 'products'
        ? `${API_BASE_URL}/material-requests/${item.RequestId}/cancel`
        : `${API_BASE_URL}/composite-requests/${item.RequestId}/cancel`;

      const res = await fetch(endpoint, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(await res.text());

    } else if (action === 'dismiss') {
      const endpoint = activeTab === 'products'
        ? `${API_BASE_URL}/material-requests/${item.RequestId}/dismiss`
        : `${API_BASE_URL}/composite-requests/${item.RequestId}/dismiss`;

      const res = await fetch(endpoint, {
        method: activeTab === 'products' ? 'PATCH' : 'POST',
        headers,
      });
      if (!res.ok) throw new Error(await res.text());
    } else if (action === 'load-to-nezo') {
      const res = await fetch(
        `${API_BASE_URL}/composite-requests/${item.RequestId}/load-to-nezo`,
        { method: 'POST', headers }
      );
      if (!res.ok) throw new Error(await res.text());

    } else if (action === 'company-dismiss') {
      const res = await fetch(
        `${API_BASE_URL}/composite-requests/${item.RequestId}/company-dismiss`,
        { method: 'POST', headers }
      );
      if (!res.ok) throw new Error(await res.text());

    } else if (action === 'request-deletion') {
      // ✅ Use the correct endpoint based on whether it's a product or composite
      const endpoint = activeTab === 'products'
        ? `${API_BASE_URL}/material-requests/${item.RequestId}/request-deletion`
        : `${API_BASE_URL}/composite-requests/${item.RequestId}/request-deletion`;

      const res = await fetch(endpoint, { method: 'POST', headers });
      if (!res.ok) throw new Error(await res.text());

      setSuccessModal({
        open: true,
        title: '📬 Deletion Request Submitted',
        message: 'Your request has been sent to an admin. They will review and delete the material from the database shortly.',
      });

      setSelectedItem(null);
      setSelectedType(null);
      loadData();
      return;
    }

    setSelectedItem(null);
    setSelectedType(null);
    if (action !== 'request-deletion') {
      loadData();
    }
  } catch (err) {
    alert('Action failed: ' + err.message);
  }
};

const confirmDeleteDraft = async () => {
  const item = confirmModal.item;
    setConfirmModal({ open: false, item: null });
    if (!item) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/composite-requests/${item.RequestId}/delete-draft`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-user-id':    user.uid,
          'x-user-role':  user.role || 'user',
          'x-region':     item.RegionId || selectedRegion || 'vquest',
        },
      });
      if (!res.ok) throw new Error(await res.text());
      loadData();
    } catch (err) {
      alert('Failed to delete draft: ' + err.message);
    }
  };

  /* ── Guards ── */
  if (!user) return (
    <div className="mm-page">
      <div className="mm-header">
        <button className="mm-back-btn" onClick={() => navigate('/dashboard')}>← Back</button>
        <h1>My Materials</h1>
      </div>
      <div className="mm-empty-state">
        <div className="mm-empty-state__icon">🔒</div>
        <p>Please log in to view your materials.</p>
      </div>
    </div>
  );

  if (loading) return (
    <div className="mm-page">
      <div className="mm-header">
        <button className="mm-back-btn" onClick={() => navigate('/user/dashboard')}>← Back</button>
        <h1>My Materials</h1>
      </div>
      <div className="mm-loading">
        <div className="mm-loading__spinner" />
        <p>Loading your submissions...</p>
      </div>
    </div>
  );

  /* ── Empty state copy ── */
  const emptyMessages = {
    Pending:   { icon: '⏳', title: 'No pending requests',  body: activeTab === 'products' ? 'Any new product submissions will appear here.' : 'Any new composite requests will appear here.' },
    Draft:     { icon: '📝', title: 'No drafts',            body: 'Drafts you save will appear here.' },
    ForReview: { icon: '🔍', title: 'Nothing under review', body: 'Materials you send for internal review will appear here.' },
    Approved:  { icon: '✅', title: 'Nothing approved yet', body: 'Items approved by an admin will appear here.' },
    Rejected:  { icon: '❌', title: 'No rejections',        body: 'Rejected items will appear here with admin feedback.' },
    Dismissed: { icon: '🗑️', title: 'Nothing dismissed',   body: 'Dismissed items will appear here.' },
    DeletionRequested: { icon: '🚨', title: 'Deletion requested', body: 'Your deletion request has been sent to an admin and is awaiting action.'},
    Deleted: { icon: '🗑️', title: 'No deleted materials', body: 'Deleted materials will appear here once an admin has removed them.'},
  };

  return (
    <div className="mm-page">

      {/* Header */}
      <div className="mm-header">
        <button className="mm-back-btn" onClick={() => navigate('/user/dashboard')}>← Back to Dashboard</button>
        <div>
          <h1 className="mm-title">My Materials</h1>
          <p className="mm-subtitle">Track your product submissions and composite material requests</p>
        </div>
        <button className="mm-refresh-btn" onClick={loadData}>🔄 Refresh</button>
      </div>

      <GuideModal
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
        src="/guides/MyMaterials_Workflow.html"
        title="How to create a material — My Materials workflow"
      />

      {/* Stats row */}
      <div className="mm-stats-row">
        <div className="mm-stat mm-stat--teal">
          <div className="mm-stat__icon">📦</div>
          <div>
            <div className="mm-stat__value">{stats.totalProducts}</div>
            <div className="mm-stat__label">Products Submitted</div>
          </div>
        </div>
        <div className="mm-stat mm-stat--emerald">
          <div className="mm-stat__icon">✅</div>
          <div>
            <div className="mm-stat__value">{stats.approvedProducts}</div>
            <div className="mm-stat__label">Products Approved</div>
          </div>
        </div>
        <div className="mm-stat mm-stat--sky">
          <div className="mm-stat__icon">🏗️</div>
          <div>
            <div className="mm-stat__value">{stats.totalComposites}</div>
            <div className="mm-stat__label">Composites Requested</div>
            {stats.totalComposites > 0 && (
              <div className="mm-stat__breakdown">
                <span className="mm-stat__breakdown-item mm-stat__breakdown-item--private">🔒 {stats.privateComposites} private</span>
                <span className="mm-stat__breakdown-item mm-stat__breakdown-item--public">🌐 {stats.publicComposites} public</span>
              </div>
            )}
          </div>
        </div>
        <div className="mm-stat mm-stat--indigo">
          <div className="mm-stat__icon">⏳</div>
          <div>
            <div className="mm-stat__value">{stats.pendingProducts + stats.pendingComposites}</div>
            <div className="mm-stat__label">Pending Review</div>
          </div>
        </div>
      </div>

      {/* Content tabs */}
      <div className="mm-tabs-row">
        <div className="mm-tabs">
          <button
            className={`mm-tab ${activeTab === 'products' ? 'mm-tab--active' : ''}`}
            onClick={() => handleTabChange('products')}
          >
            📦 Product Submissions ({productSubmissions.length})
          </button>
          <button
            className={`mm-tab ${activeTab === 'composites' ? 'mm-tab--active' : ''}`}
            onClick={() => handleTabChange('composites')}
          >
            🏗️ Composite Requests ({compositeRequests.length})
          </button>
        </div>

        <button className="mm-guide-btn mm-guide-btn--inline" onClick={() => setShowTutorial(true)}>
          📘 How to create a material
        </button>

        {/* Visibility + search filters */}
        <div className="mm-filters">
          {activeTab === 'composites' && (
            <select
              className="mm-filter-select"
              value={visibilityFilter}
              onChange={(e) => setVisibilityFilter(e.target.value)}
            >
              <option value="all">All Visibility</option>
              <option value="private">🔒 Private</option>
              <option value="public">🌐 Public</option>
            </select>
          )}

          {categoryOptions.length > 0 && (
            <select
              className="mm-filter-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categoryOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}

          {unitOptions.length > 0 && (
            <select
              className="mm-filter-select"
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
            >
              <option value="all">All Units</option>
              {unitOptions.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          )}

          {createdByOptions.length > 0 && (
            <select
              className="mm-filter-select"
              value={createdByFilter}
              onChange={(e) => setCreatedByFilter(e.target.value)}
            >
              <option value="all">All Creators</option>
              {createdByOptions.map((who) => (
                <option key={who} value={who}>{who}</option>
              ))}
            </select>
          )}

          <select
            className="mm-filter-select"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              if (e.target.value !== 'all') setDateExact('');
            }}
            disabled={!!dateExact}
            title={dateExact ? 'Clear the specific date to use a range' : 'Filter by date range'}
          >
            <option value="all">Any date</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>

          <input
            type="date"
            className="mm-filter-date"
            value={dateExact}
            onChange={(e) => {
              setDateExact(e.target.value);
              if (e.target.value) setDateFilter('all');
            }}
            title="Filter by a specific date"
          />
          {dateExact && (
            <button
              type="button"
              className="mm-filter-date-clear"
              onClick={() => setDateExact('')}
              title="Clear date"
            >
              ✕
            </button>
          )}

          <input
            type="text"
            className="mm-search-input"
            placeholder="Search by name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <select
            className="mm-filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            title="Sort order"
          >
            <option value="date">Newest first</option>
            <option value="name-asc">Name A–Z</option>
            <option value="name-desc">Name Z–A</option>
          </select>

          <div className="mm-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`mm-view-btn ${viewMode === 'grid' ? 'mm-view-btn--active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              ▦ Grid
            </button>
            <button
              type="button"
              className={`mm-view-btn ${viewMode === 'list' ? 'mm-view-btn--active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              ☰ List
            </button>
          </div>
        </div>
      </div>

      {/* Status tabs */}
      <StatusTabs
        items={allItemsForTab}
        activeStatus={currentStatusTab}
        onChange={(s) => {
          setCurrentStatusTab(s);
          setCategoryFilter('all');
          setUnitFilter('all');
          setCreatedByFilter('all');
          setDateFilter('all');
          setDateExact('');
        }}
      />

      {error && (
        <div className="mm-error-banner">
          <span>⚠️ {error}</span>
          <button onClick={loadData}>Retry</button>
        </div>
      )}

      {/* Items */}
      <div className="mm-items">
        {currentItems.length === 0 ? (
          <div className="mm-empty-state">
            <div className="mm-empty-state__icon">{emptyMessages[currentStatusTab]?.icon}</div>
            <h3>{emptyMessages[currentStatusTab]?.title}</h3>
            <p>{emptyMessages[currentStatusTab]?.body}</p>
            {currentStatusTab === 'Pending' && (
              <button
                className="mm-empty-state__btn"
                onClick={() => navigate(activeTab === 'products' ? '/material-requests' : '/composite-requests')}
              >
                {activeTab === 'products' ? '+ Submit Product' : '+ Build Composite'}
              </button>
            )}
          </div>
        ) : (
          <div className={`mm-card-grid ${viewMode === 'list' ? 'mm-card-grid--list' : ''}`}>
            {currentItems.map((item) => {
              const isProduct   = activeTab === 'products';
              const name = isProduct
                ? (item.MaterialData?.Name || 'Unnamed Product')
                : (
                    item.MaterialData?.BaseMaterialName   ||
                    item.MaterialData?.OriginalMaterialName ||
                    item.MaterialData?.SearchedMaterialName ||
                    item.MaterialData?.Name               ||
                    'Unnamed Composite'
                  );
              const status      = getStatus(item.Status);
              const quantityUnit =
                item.MaterialData?.QuantityUnit ||
                item.MaterialData?.QuantityTypeName ||
                item.MaterialData?.QuantityType ||
                item.QuantityUnit ||
                item.QuantityTypeName ||
                null;
              const lineItemCnt = Array.isArray(item.LineItemsData) ? item.LineItemsData.length : 0;
              const visibility  = item.Visibility || 'public';
              const canEdit     = !isProduct && (
                item.Status === 'Draft' ||
                item.Status === 'ForReview' ||
                (item.Status === 'Pending' && canReview)
              );

              return (
                <div
                  key={item.RequestId}
                  className={`mm-card ${visibility === 'private' ? 'mm-card--private' : ''}`}
                  onClick={() => openDetail(item, isProduct ? 'product' : 'composite')}
                  onContextMenu={(e) => openContextMenu(e, item, isProduct)}
                >
                  <div className="mm-card__header">
                    <div className="mm-card__type-icon">{isProduct ? '📦' : '🏗️'}</div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {item.Environment === 'staging' && (
                        <span className="env-badge" title="Made from staging Nezo — only shows in staging">Staging</span>
                      )}
                      {!isProduct && <VisibilityBadge visibility={visibility} />}
                      <div className="mm-card__status" style={{ color: status.color, background: status.bg }}>
                        {status.icon} {status.label}
                      </div>
                    </div>
                  </div>

                  <h3 className="mm-card__name">{name}</h3>

                  <div className="mm-card__meta">
                    <span className="mm-card__region">🌏 {item.RegionName || regionNameMap[selectedRegion] || 'Unknown'}</span>
                    <span className="mm-card__time">🕐 {timeAgo(item.SubmittedAt)}</span>
                    {quantityUnit && (
                      <span style={{ fontSize: '0.75em', fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                        · {quantityUnit}
                      </span>
                    )}
                    {item.MaterialData?.CategoryId && (
                      <span style={{ fontSize: '0.75em', fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                        · {resolveCategory(item.MaterialData.CategoryId)}
                      </span>
                    )}
                  </div>

                  <PeoplePills item={item} />

                  {isProduct && item.MaterialData?.Description && (
                    <p className="mm-card__desc">
                      {item.MaterialData.Description.length > 100
                        ? item.MaterialData.Description.substring(0, 100) + '...'
                        : item.MaterialData.Description}
                    </p>
                  )}

                  {!isProduct && lineItemCnt > 0 && (
                    <div className="mm-card__line-count">📋 {lineItemCnt} line items</div>
                  )}

                  {canEdit && (
                    <button
                      className="mm-card__edit-btn"
                      onClick={(e) => { e.stopPropagation(); handleEditRequest(item); }}
                      title="Edit line items and resubmit"
                    >
                      ✏️ Edit &amp; Resubmit
                    </button>
                  )}

                  {item.Status === 'Draft' && (
                    <button
                      className="mm-card__edit-btn"
                      style={{ background: '#fef2f2', color: '#ef4444', borderColor: '#fecaca', marginTop: 6 }}
                      onClick={(e) => handleDeleteDraft(item, e)}
                      title="Delete this draft"
                    >
                      🗑️ Delete Draft
                    </button>
                  )}

                  {item.Status === 'ForReview' && (
                    <div
                      className="mm-card__private-note"
                      style={{ background: '#fffbeb', color: '#92400e', borderColor: '#fcd34d' }}
                    >
                      🔍 Saved for internal review
                    </div>
                  )}

                  {!isProduct && item.Status === 'Pending' && (
                    <div className="mm-card__private-note">
                      {visibility === 'private'
                        ? 'Awaiting admin approval before it appears in your account'
                        : 'Awaiting admin approval before it appears for your region'}
                    </div>
                  )}

                  {item.Status === 'Rejected' && (
                    <div className="mm-card__rejected-note">⚠️ Changes requested — view details for feedback</div>
                  )}

                  {/* Cancel for Pending — products: anyone; composites: reviewers/admins only */}
                  {item.Status === 'Pending' && (isProduct || canReview) && (
                    <button
                      className="mm-card__edit-btn"
                      style={{ background: '#fef2f2', color: '#ef4444', borderColor: '#fecaca', marginTop: 6 }}
                      onClick={(e) => { e.stopPropagation(); setConfirmModal({ open: true, item, action: 'cancel' }); }}
                    >
                      ✖ Cancel Request
                    </button>
                  )}

                  {/* Request Deletion for private Approved only */}
                  {item.Status === 'Approved' && item.Visibility === 'private' && (
                    <button
                      className="mm-card__edit-btn"
                      style={{ background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca', marginTop: 4, fontSize: 12 }}
                      onClick={(e) => { e.stopPropagation(); setConfirmModal({ open: true, item, action: 'request-deletion' }); }}
                    >
                      🗑️ Request Deletion
                    </button>
                  )}

                  <div className="mm-card__footer">
                    <span className="mm-card__id">ID: {item.RequestId?.substring(0, 8)}...</span>
                    <span className="mm-card__view">View Details →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedItem && (
        <DetailModal
          item={selectedItem}
          type={selectedType}
          onClose={() => { setSelectedItem(null); setSelectedType(null); }}
          onEditRequest={handleEditRequest}
          onDeleteDraft={handleDeleteDraft}
          onCancel={(item) => setConfirmModal({ open: true, item, action: 'cancel' })}
          onRequestDeletion={(item) => setConfirmModal({ open: true, item, action: 'request-deletion' })}
          onUpdateRequest={handleUpdateRequest}
          onCopy={handleCopyRequest}
          canReview={canReview}
          onLoadToNezo={(item) => setConfirmModal({ open: true, item, action: 'load-to-nezo' })}
          onCompanyDismiss={(item) => setConfirmModal({ open: true, item, action: 'company-dismiss' })}
          resolveCategory={resolveCategory}
        />
      )}

      <ContextMenu menu={contextMenu} onClose={closeContextMenu} />

      {showEditModal && editItem && (
        <EditCompositeModal
          item={editItem}
          onClose={handleEditClose}
          onResubmitted={handleResubmitted}
          isUpdateMode={editItem.IsUpdate === 1}
          updateTargetId={editItem.UpdateTargetId || editItem.QuantityUnitId || null}
        />
      )}
      {showUpdateModal && updateItem && (
      <EditCompositeModal
        item={updateItem}
        onClose={() => { setShowUpdateModal(false); setUpdateItem(null); }}
        onResubmitted={() => { loadData(); setShowUpdateModal(false); setUpdateItem(null); }}
        isUpdateMode={true}
        updateTargetId={updateItem.QuantityUnitId || null}
      />
    )}

        <ConfirmModal
          isOpen={confirmModal.open}
          title={
            confirmModal.action === 'cancel'           ? '⚠️ Cancel Request' :
            confirmModal.action === 'dismiss'          ? '🗑️ Dismiss Approved' :
            confirmModal.action === 'request-deletion' ? '🗑️ Request Deletion' :
            confirmModal.action === 'load-to-nezo'     ? '🚀 Load to Nezo' :
            confirmModal.action === 'company-dismiss'  ? '🗑️ Dismiss Request' :
                                                        '🗑️ Delete Draft'
          }
          message={
            confirmModal.action === 'cancel'
              ? 'Are you sure? This will permanently delete your pending request and cannot be undone.'
            : confirmModal.action === 'dismiss'
              ? 'This will remove this approved request from your view. You can find it under Dismissed if needed.'
            : confirmModal.action === 'request-deletion'
              ? 'This will notify an admin to permanently delete this private material from the database. This cannot be undone.'
            : confirmModal.action === 'load-to-nezo'
              // Private composites auto-approve on the server, so don't promise
              // a Nezo review that will never happen.
              ? (confirmModal.item?.Visibility === 'private'
                  ? 'Send this request to Nezo? Private materials are approved automatically and will be available in the creator’s library straight away.'
                  : 'Send this request to Nezo for approval? It will move to Pending and our team will review it.')
            : confirmModal.action === 'company-dismiss'
              ? 'Dismiss this request for your company? It will be marked as Dismissed.'
              : 'Are you sure you want to delete this draft? This cannot be undone.'
          }
          confirmLabel={
            confirmModal.action === 'cancel'           ? 'Yes, Cancel Request' :
            confirmModal.action === 'dismiss'          ? 'Yes, Dismiss' :
            confirmModal.action === 'request-deletion' ? 'Yes, Request Deletion' :
            confirmModal.action === 'load-to-nezo'     ? 'Yes, Send to Nezo' :
            confirmModal.action === 'company-dismiss'  ? 'Yes, Dismiss' :
                                                        'Yes, Delete Draft'
          }
          onConfirm={confirmModal.action ? handleConfirmAction : confirmDeleteDraft}
          onCancel={() => setConfirmModal({ open: false, item: null, action: null })}
        />
        {/* Success Modal */}
        {successModal.open && (
          <div className="mm-modal-overlay" onClick={() => setSuccessModal({ open: false, title: '', message: '' })}>
            <div className="mm-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
              <div className="mm-modal__header">
                <h2 style={{ fontSize: 18 }}>{successModal.title}</h2>
              </div>
              <div className="mm-modal__body" style={{ padding: '20px 24px' }}>
                <p style={{ color: '#374151', lineHeight: 1.6 }}>{successModal.message}</p>
              </div>
              <div className="mm-modal__footer">
                <div />
                <button
                  className="mm-modal__btn"
                  style={{ background: '#ecfdf5', color: '#10b981', border: '1px solid #6ee7b7', fontWeight: 600 }}
                  onClick={() => setSuccessModal({ open: false, title: '', message: '' })}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

export default MyMaterialsPage;